/**
 * AutoTranslated — drop-in wrapper that shows a source string translated
 * into the user's currently-selected UI language. Supports either a plain
 * text mode (children) or a raw HTML mode (`html` prop). Emits a tiny
 * "Translated" chip so users always know when they're reading a translation.
 */
import React from 'react';
import { useTranslation } from 'react-i18next';
import { Languages, Loader2 } from 'lucide-react';
import { useAutoTranslate } from '../../hooks/useAutoTranslate';

const Badge = ({ isTranslating, isTranslated }) => {
  const { t } = useTranslation();
  if (!isTranslating && !isTranslated) return null;
  return (
    <span
      className="inline-flex items-center gap-1 rounded-full border border-primary/30 bg-primary/10 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-primary"
      data-testid="auto-translated-badge"
    >
      {isTranslating ? (
        <>
          <Loader2 className="w-3 h-3 animate-spin" />
          {t('common.translating', 'Translating…')}
        </>
      ) : (
        <>
          <Languages className="w-3 h-3" />
          {t('common.translated', 'Translated')}
        </>
      )}
    </span>
  );
};

/** Plain-text renderer. `children` should be a string. */
export const AutoTranslated = ({
  children,
  as: Tag = 'span',
  className = '',
  showBadge = true,
  testid,
}) => {
  const source = typeof children === 'string' ? children : '';
  const { translated, isTranslating, isTranslated } = useAutoTranslate(source);
  return (
    <>
      <Tag className={className} data-testid={testid}>
        {translated || source}
      </Tag>
      {showBadge && (isTranslating || isTranslated) && (
        <span className="ml-2 align-middle">
          <Badge isTranslating={isTranslating} isTranslated={isTranslated} />
        </span>
      )}
    </>
  );
};

/** HTML renderer for rich text (Quill / TipTap output). Translates the raw
 *  HTML string as one blob so tags survive round-trip. */
export const AutoTranslatedHtml = ({
  html,
  className = '',
  showBadge = true,
  testid,
}) => {
  const src = typeof html === 'string' ? html : '';
  const { translated, isTranslating, isTranslated } = useAutoTranslate(src);
  return (
    <div className={className} data-testid={testid}>
      {showBadge && (isTranslating || isTranslated) && (
        <div className="mb-2">
          <Badge isTranslating={isTranslating} isTranslated={isTranslated} />
        </div>
      )}
      <div
        dangerouslySetInnerHTML={{ __html: translated || src }}
      />
    </div>
  );
};

export default AutoTranslated;
